import { portalText } from "../../i18n/portal.js";
/**
 * Area `voice-sample` of the console gateway — design §26 (the product owner's request of 2026-10-02).
 *
 * The voice chooser's Play: `GET /v1/voices/{voice_id}/sample?language=el` answers a WAV of the voice
 * saying the language's sample line. The generated client's `send` reads every answer as text, which
 * is exactly wrong for audio — so, like the SSE streams (`../stream.ts`), this one operation is reached
 * with the transport directly. That does **not** mean the URL is hand-written: {@link voiceSampleUrlOf}
 * resolves the path out of `API_OPERATION`, the generated operation table, so a path the spec does not
 * document cannot be fetched and a path that moves in the spec moves here too. The answer is a `Blob`
 * the chooser plays through an object URL — which also spares the browser range requests against the
 * API.
 *
 * plan-015 GRT (gap G2, owner answer 11, design §28.5.2): "Play <voice> saying the greeting" —
 * `POST /v1/voices/{voice_id}/greeting-sample { language_code, text }` answers a WAV of the voice saying
 * the owner's own typed greeting. Reached the same way, for the same reason; the greeting travels in the
 * body, never in the URL. A POST with the session cookie carries the CSRF header, as the generated client's
 * writes do.
 *
 * The register is EMPTY: both operations are served.
 */
// glossary-lint-disable-next-line credential -- the generated client's type name, renamed on import
import { API_OPERATION, type ApiCredential as ApiPersonCredential } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import type { GatewayGap } from "./slice-context.js";

declare module "../gateway.js" {
  interface ConsoleGateway {
    /**
     * The voice saying `languageCode`'s sample line, as audio. Raises {@link VoiceSampleUnavailableError}
     * when there is none (404) or it cannot be heard right now (503, a network failure).
     */
    voiceSampleAudio(voiceId: string, languageCode: string): Promise<Blob>;
    /**
     * plan-015 GRT: the voice saying `text` — the greeting as typed — in `languageCode`, as audio. Raises
     * {@link VoiceSampleUnavailableError} when the voice does not speak the language (404), the greeting
     * cannot be heard right now (503, a network failure), too many new greetings were played this hour
     * (429) or the text is refused (400).
     */
    greetingSampleAudio(voiceId: string, languageCode: string, text: string): Promise<Blob>;
  }
}

export const voiceSampleGap: readonly GatewayGap[] = [];

/** The sample cannot be played: the chooser says "Sample unavailable". `status` is null for no answer. */
export class VoiceSampleUnavailableError extends Error {
  override readonly name = "VoiceSampleUnavailableError";
  readonly status: number | null;
  constructor(status: number | null) {
    super(status === null ? portalText("shell", "the_voice_sample_could_not_be_fetched_d996079e") : portalText("shell", "the_voice_sample_answered_value_9ff1317e", { v0: String(String(status)) }));
    this.status = status;
  }
}

const OPERATION_ID = "getVoiceSample";
const GREETING_OPERATION_ID = "createGreetingSample";

/** One operation's path from the generated table, with the voice put in. */
function voicePathOf(operationId: string, voiceId: string): string {
  const operation = API_OPERATION.find((one) => one.operationId === operationId);
  if (operation === undefined) {
    throw new Error(`"${operationId}" is not an operation of the assembled OpenAPI document; re-run \`pnpm api:build-spec\`.`);
  }
  return operation.path.replace("{voice_id}", encodeURIComponent(voiceId));
}

/** The sample's absolute URL, built from the spec's own path template. */
export function voiceSampleUrlOf(baseUrl: string, voiceId: string, languageCode: string): string {
  const url = new URL(`${baseUrl}${voicePathOf(OPERATION_ID, voiceId)}`);
  url.searchParams.set("language", languageCode);
  return url.toString();
}

/** The greeting sample's absolute URL (plan-015 GRT). The greeting is never in it: it goes in the body. */
export function greetingSampleUrlOf(baseUrl: string, voiceId: string): string {
  return new URL(`${baseUrl}${voicePathOf(GREETING_OPERATION_ID, voiceId)}`).toString();
}

/** Reads an audio answer: a 200's bytes, or {@link VoiceSampleUnavailableError} with the status. */
async function audioOf(answer: Response): Promise<Blob> {
  if (answer.status !== 200) {
    await answer.body?.cancel();
    throw new VoiceSampleUnavailableError(answer.status);
  }
  return answer.blob();
}

export function voiceSampleApiSlice(
  baseUrl: string,
  personCredential: ApiPersonCredential,
  fetchOf: typeof globalThis.fetch,
): Pick<ConsoleGateway, "voiceSampleAudio" | "greetingSampleAudio"> {
  return {
    async voiceSampleAudio(voiceId: string, languageCode: string): Promise<Blob> {
      const header: Record<string, string> = { accept: "audio/wav" };
      if (personCredential.kind === "bearer") header["authorization"] = `Bearer ${personCredential.key}`;
      // Built outside the try: an operation missing from the spec is a programming fault, not "unavailable".
      const url = voiceSampleUrlOf(baseUrl, voiceId, languageCode);
      let answer: Response;
      try {
        answer = await fetchOf(url, {
          method: "GET",
          headers: header,
          // The console's session cookie, as every generated-client call sends it (a GET needs no CSRF token).
          // glossary-lint-disable-next-line credential -- the Fetch API's own RequestInit field
          credentials: personCredential.kind === "cookie" ? "include" : "omit",
        });
      } catch {
        throw new VoiceSampleUnavailableError(null);
      }
      return audioOf(answer);
    },

    async greetingSampleAudio(voiceId: string, languageCode: string, text: string): Promise<Blob> {
      const header: Record<string, string> = { accept: "audio/wav", "content-type": "application/json" };
      if (personCredential.kind === "bearer") header["authorization"] = `Bearer ${personCredential.key}`;
      // A write with the session cookie carries the auth_session's CSRF token, read now (it can change).
      else header["x-wetalk-csrf"] = personCredential.csrfToken;
      const url = greetingSampleUrlOf(baseUrl, voiceId);
      let answer: Response;
      try {
        answer = await fetchOf(url, {
          method: "POST",
          headers: header,
          body: JSON.stringify({ language_code: languageCode, text }),
          // glossary-lint-disable-next-line credential -- the Fetch API's own RequestInit field
          credentials: personCredential.kind === "cookie" ? "include" : "omit",
        });
      } catch {
        throw new VoiceSampleUnavailableError(null);
      }
      return audioOf(answer);
    },
  };
}
